'use client';

import { useMemo, useState } from 'react';
import { resolveCreatedValue, withCreateOption } from '@highlight-cp/core-ui';
import type {
  ComboboxData,
  CreatableSelectProps as CreatableFlowProps,
} from '@highlight-cp/core-ui';

interface UseCreatableOptionsProps extends CreatableFlowProps {
  data: ComboboxData<string> | undefined;
}

/**
 * Search state and create-option plumbing shared by the creatable Select and MultiSelect
 * compositions. Internal — not exported from the barrel.
 */
export function useCreatableOptions({
  creatable,
  data,
  getCreateLabel,
  onCreate,
}: UseCreatableOptionsProps) {
  const [search, setSearch] = useState('');

  const augmentedData = useMemo(
    () => (creatable ? withCreateOption(data, search, getCreateLabel) : data),
    [creatable, data, search, getCreateLabel],
  );

  /** Runs the caller's `onCreate` for the current query and returns the value to select. */
  const resolveCreate = () => {
    const query = search.trim();
    return resolveCreatedValue(onCreate?.(query), query);
  };

  const searchProps = creatable ? { searchValue: search, onSearchChange: setSearch } : {};

  return { augmentedData, searchProps, resolveCreate };
}
